import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, TriangleAlert } from 'lucide-react';
import { ProgressCircle } from './ui/ProgressCircle';
import {
  clampPercentage,
  formatTwoDigitCount,
} from 'features/DigitalMarketing/utils/numberFormat';
import { DMButton, DMDiv, DMText } from './Primitives';

type SeoHealthData = {
  LOW?: number;
  MEDIUM?: number;
  HIGH?: number;
  CRITICAL?: number;
  overall_seo_percentage?: number;
  total_issues?: number;
};

interface SeoHealthDetailedCardProps {
  data?: SeoHealthData;
}

export const SeoHealthDetailedCard = ({ data }: SeoHealthDetailedCardProps) => {
  const navigate = useNavigate();
  const score = clampPercentage(data?.overall_seo_percentage || 0);
  const totalIssues = data?.total_issues || 0;
  const issueSummary = [
    { count: data?.LOW || 0, label: 'LOW', color: 'text-[#00EA73]' },
    { count: data?.MEDIUM || 0, label: 'MEDIUM', color: 'text-[#FFF100]' },
    { count: data?.HIGH || 0, label: 'HIGH', color: 'text-[#FF6F9B]' },
    { count: data?.CRITICAL || 0, label: 'CRITICAL', color: 'text-[#FF3B30]' },
  ];

  return (
    <DMDiv className="relative w-full h-full min-h-[27vh] rounded-[2vh] bg-gradient-to-br from-[#2E2A93] via-[#3530C0] to-[#4736E0] px-[2.45vh] py-[2.7vh] text-white flex flex-col shadow-sm overflow-hidden">
      <DMDiv className="flex items-start justify-between shrink-0 pr-[5.8vh]">
        <DMText
          as="h3"
          className="text-[1.85vh] font-extrabold leading-none text-white"
        >
          SEO Health
        </DMText>
        <DMButton
          type="button"
          aria-label="Open SEO health details"
          onClick={() => navigate('/digital-marketing/seo')}
          className="absolute right-[2.45vh] top-[1.6vh] w-[5.6vh] h-[5.6vh] rounded-full bg-white text-[#4B42DB] flex items-center justify-center shadow-sm hover:bg-white/90 active:scale-95 transition"
        >
          <ArrowUpRight className="w-[2.45vh] h-[2.45vh]" strokeWidth={2} />
        </DMButton>
      </DMDiv>

      <DMDiv className="flex flex-1 items-end justify-evenly gap-[0.8vh] pt-[0.3vh]">
        <DMDiv className="relative shrink-0 w-[23.2vh] h-[23.2vh] -ml-[1.8vh] -mb-[1.8vh]">
          <ProgressCircle score={score} />
          <DMDiv className="absolute inset-0 flex items-center justify-center">
            <DMText
              as="span"
              className="text-[4.4vh] font-extrabold leading-none text-[#00EA73]"
            >
              {score}
              <DMText
                as="span"
                className="ml-[0.15vh] align-middle text-[1.8vh] font-medium text-white"
              >
                %
              </DMText>
            </DMText>
          </DMDiv>
        </DMDiv>

        <DMDiv className="flex w-[43%] justify-center flex-col gap-[2.15vh] pb-[1.45vh]">
          <DMDiv className="flex flex-col items-center gap-[1.25vh] mb-[0.5vh]">
            <TriangleAlert
              className="w-[3.55vh] h-[3.55vh] text-[#FFDD1F]"
              strokeWidth={2.35}
            />
            <DMText className="flex items-center justify-center gap-[0.75vh] whitespace-nowrap">
              <DMText
                as="span"
                className="text-[2.35vh] font-extrabold leading-none"
              >
                {totalIssues}
              </DMText>
              <DMText
                as="span"
                className="text-neutral-40 text-[1.6vh] font-medium uppercase tracking-[0.13em] leading-none text-white/78"
              >
                Issues Found
              </DMText>
            </DMText>
          </DMDiv>

          {issueSummary.map(item => (
            <DMDiv
              key={item.label}
              className="grid grid-cols-[3vh_1fr] items-center gap-[3.2vh]"
            >
              <DMText
                as="span"
                className="text-left text-[1.65vh] font-extrabold leading-none text-white"
              >
                {formatTwoDigitCount(item.count)}
              </DMText>
              <DMText
                as="span"
                className={`text-right text-[1.15vh] font-extrabold leading-none ${item.color}`}
              >
                {item.label}
              </DMText>
            </DMDiv>
          ))}
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};

